import type { BrowserWindowConstructorOptions } from '@core/compositions/Electron'
import { useElectron } from '@core/compositions/Electron'
import { isEmpty } from '@core/Utils'
import router from '@/router'
import { PATH_SEPARATOR, Router } from '@core/Constants'
import { getMicroAppPreviewBaseUrl, getMicroAppBaseUrl } from '@core/application/AppUtils'

export interface IPageManager {
  /**
   *  Cause browser go to the page in current window(table).
   *
   * @param relativePath Micro App's relative path(router) which will be push to main app's router
   * @return Promise<boolean> true if succeeded, else false
   */
  goto(relativePath: string): Promise<boolean>

  /**
   * Cause the main frame to open a new window(table) to show the micro app's page which relative to the path.
   *
   * @param openPath Micro App's relative path(router) which will be push to main app's router
   */
  openInNewWindow(openPath: string, browserWindowOptions?: BrowserWindowConstructorOptions): void
}

// Export for TEST ONLY, DO NOT use it directly in production code. Using ApplicationManager.getPageManager instead
export class PageManager implements IPageManager {
  #appEntry = ''

  /**
   * Constructor
   * @param entry the entry path of the micro app
   */
  constructor(entry: string) {
    if (!isEmpty(entry)) {
      this.#appEntry = entry
    } else {
      console.error('pageManager init inappropriately')
    }
  }

  async goto(relativePath: string, isReplace?: boolean): Promise<boolean> {
    return await this.#gotoImpl(relativePath, false, isReplace)
  }

  async gotoPreview(relativePath: string, isReplace?: boolean): Promise<boolean> {
    return await this.#gotoImpl(relativePath, true, isReplace)
  }

  async #gotoImpl(relativePath: string, isPreview = false, isReplace = false): Promise<boolean> {
    if (isEmpty(this.#appEntry)) {
      return false
    }

    const logPrefix = isPreview ? 'preview' : ''
    const baseUrlFun = isPreview ? getMicroAppPreviewBaseUrl : getMicroAppBaseUrl
    const path = Array.of(baseUrlFun(true), this.#appEntry, relativePath).join(PATH_SEPARATOR)

    try {
      console.log(`${logPrefix} page.goto: ${path}`)
      const isRouterReplace = router.currentRoute.value.name === Router.ROUTE_NAME_MAIN_APP_HOME || isReplace

      console.log(
        `${logPrefix} page.goto input ${isReplace} and router method: ${isRouterReplace ? 'replace' : 'push'}`
      )

      isRouterReplace ? await router.replace(path) : await router.push(path)

      return true
    } catch (error) {
      console.error(`got error when go to ${logPrefix} ${relativePath} with error: ${error}`)

      return false
    }
  }

  openInNewWindow(openPath: string, browserWindowOptions: BrowserWindowConstructorOptions = {}): void {
    const { isFullPath } = browserWindowOptions

    if (isEmpty(this.#appEntry) && !isFullPath) {
      return
    }
    let path = isFullPath
      ? openPath
      : Array.of(getMicroAppPreviewBaseUrl(), this.#appEntry, openPath).join(PATH_SEPARATOR)
    const pcEnv = useElectron()

    if (pcEnv) {
      path = isFullPath ? path : Array.of(window.location.origin, path).join('')
      pcEnv.openInNewWindow(path, browserWindowOptions)
    } else {
      window.open(path)
    }
  }
}
